// dependencies
import { DndContext, DragEndEvent, Modifier, MouseSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import {
	restrictToParentElement as restrictToParentElementModifier,
	restrictToVerticalAxis as restrictToVerticalAxisModifier,
} from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import React, { ReactNode } from "react";

// components
import SortableItem from "./SortableItem";

interface DragAndDropProps {
	/**
	 * Callback for when the drag and drop ends
	 */
	handleOnDragEnd: (event: DragEndEvent) => void;
	/**
	 * array of item ids
	 */
	itemIds: string[];
	/**
	 * class name for the items container
	 */
	itemsContainerClassName?: string;
	/**
	 * array of items to be dragged
	 */
	draggableItems?: any;
	/**
	 * array of nodes to be dragged, this is needed if the draggable items are not just text and have some draggable handler
	 * it should be used instead of draggableItems and formatDraggableItems when an icon is used as the activator/ if more functionality is needed as an override
	 */
	draggableNodeItems?: ReactNode[];
	/**
	 * function to format the draggable items
	 */
	formatDraggableItems?: (item: any, index: number) => ReactNode;
	/**
	 * whether or not to enable auto scroll
	 */
	autoSrcoll?: boolean;
	/**
	 * key to use for the draggable items, defaults to "id"
	 */
	keyValue?: string;
	/**
	 * test id for the component
	 */
	testId?: string;
	/**
	 * ref for list container
	 */
	itemsContainerRef?: any;

	/**
	 * restrict the drag and drop to the parent element
	 */
	restrictToParentElement?: boolean;

	/**
	 * restrict the drag and drop to the vertical axis
	 */
	restrictToVerticalAxis?: boolean;

	/**
	 * whether or not to use an unordered list
	 */
	useUnorderedList?: boolean;
}

/**
 * Component to allow reordering a list of items by dragging and dropping
 */
const DragAndDrop = (props: DragAndDropProps) => {
	const {
		handleOnDragEnd,
		itemIds,
		itemsContainerClassName,
		draggableItems = [],
		draggableNodeItems = [],
		formatDraggableItems,
		autoSrcoll = false,
		keyValue = "id",
		testId = "",
		itemsContainerRef = null,
		restrictToParentElement = false,
		restrictToVerticalAxis = false,
		useUnorderedList = false,
	} = props;

	const mouseSensor = useSensor(MouseSensor, {
		activationConstraint: {
			distance: 10, // Enable sort function when dragging 10px
		},
	});

	const sensors = useSensors(mouseSensor);

	const modifiers: Modifier[] = [];
	if (restrictToParentElement) {
		modifiers.push(restrictToParentElementModifier);
	}

	if (restrictToVerticalAxis) {
		modifiers.push(restrictToVerticalAxisModifier);
	}
	const Container = useUnorderedList ? "ul" : "div";

	return (
		<div>
			<DndContext
				onDragEnd={handleOnDragEnd}
				collisionDetection={closestCenter}
				sensors={sensors}
				autoScroll={autoSrcoll}
				modifiers={modifiers}
			>
				<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
					<Container className={itemsContainerClassName} data-testid={testId} ref={itemsContainerRef}>
						{draggableItems.map((item, index) => {
							return (
								<SortableItem key={item[keyValue]} id={item[keyValue]}>
									{formatDraggableItems && formatDraggableItems(item, index)}
								</SortableItem>
							);
						})}
						{draggableNodeItems.length > 0 && draggableNodeItems.map((item) => item)}
					</Container>
				</SortableContext>
			</DndContext>
		</div>
	);
};

export default DragAndDrop;
